import { BarChart2, ChevronDown, ChevronUp, Clock, Search, Tag, XCircle } from 'react-feather';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import flatten from 'lodash/flatten';
import intersection from 'lodash/intersection';
import map from 'lodash/map';
import styled, { css } from 'styled-components';

import {
  GetOrderFiltersQuery as GetOrderFilters,
  Locale,
  ParticipantFieldScope,
  PurchasePaymentStatus,
} from '__generated__/graphql';

import { ParticipantFieldFilter } from '../Registrations/RegistrationFilters';
import { isEmpty, toggleInList } from '../../../common/helpers';
import EventPicker, { Event as SelectedEvent } from '../../Events/EventPicker';
import GetOrderFiltersQuery from './GetOrderFiltersQuery';
import ParticipantFieldFiltersForm, { ParticipantFieldFilters } from '../ParticipantFieldFiltersForm';
import SellableLabel from '../../../common/Common/SellableLabel';
import UI from '../../../common/UI';
import config from '../../config';
import useLocale from '../../../common/useLocale';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';

export type OrderStats = GetOrderFilters['project']['order_stats'];

export interface OrderFilterParams {
  event?: string | null;
  search?: string | null;
  ticket_categories?: string[];
  tickets?: string[];
  time_slots?: string[];
  products?: string[];
  product_variants?: string[];
  promotions?: string[];
  invitations?: string[];
  discounts?: string[];
  coupons?: string[];
  lotteries?: string[];
  confirmed_lotteries?: string[];
  rejected_lotteries?: string[];
  payment_statuses?: PurchasePaymentStatus[];
  participant_fields?: ParticipantFieldFilter[];
  participant_field_choices?: string[];
  assigned?: boolean | null;
  mail_opt_ins?: string[];
  locales?: Locale[];
}

export const defaultFilters: OrderFilterParams = {
  event: null,
  search: null,
  ticket_categories: [],
  tickets: [],
  time_slots: [],
  products: [],
  product_variants: [],
  promotions: [],
  invitations: [],
  discounts: [],
  coupons: [],
  lotteries: [],
  confirmed_lotteries: [],
  rejected_lotteries: [],
  payment_statuses: [],
  participant_fields: [],
  participant_field_choices: [],
  assigned: null,
  mail_opt_ins: [],
  locales: [],
};

/**
 * Types of filters that can be applied to orders.
 * */
type OrderFilterType = (
  'event' | 'search' | 'tickets' | 'time_slots' | 'products' | 'product_variants' | 'promotions' | 'payment_statuses'
  | 'participant_fields' | 'participant_field_choices' | 'invitations' | 'discounts' | 'coupons' | 'lotteries'
  | 'confirmed_lotteries' | 'rejected_lotteries' | 'assigned' | 'mail_opt_ins' | 'locales'
);

export interface OrderFiltersProps {
  types?: OrderFilterType[];
  filters: OrderFilterParams;
  onChange: (update: (filters: OrderFilterParams) => OrderFilterParams) => void;
  onStatsChange?: (stats: OrderStats) => void;
  readOnly?: boolean;
  showTotals?: boolean;
  showResetButton?: boolean;
}

const OrderFilters = ({
  types,
  filters: filtersProp,
  onChange,
  onStatsChange,
  readOnly = false,
  showTotals = true,
  showResetButton = true,
}: OrderFiltersProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();
  const formatTimeSlot = useTimeSlotFormatter();

  const enabled = (type: OrderFilterType) => !types || types.includes(type);

  const filters = Object.keys(defaultFilters).reduce((filters, key: keyof OrderFilterParams) => ({
    ...filters,
    [key]: key in filtersProp ? filtersProp[key] : filters[key],
  }), defaultFilters);

  const searchRef = useRef<HTMLInputElement>();

  // Fetches the filters (tickets etc.)
  const { data, loading } = useQuery(
    GetOrderFiltersQuery,
    {
      variables: {
        events: filters.event ? [filters.event] : [],
        search: filters.search,
        participant_fields: filters.participant_fields,
        participant_field_choices: filters.participant_field_choices,
        purchase_filters: {
          ticket_categories: filters.ticket_categories,
          tickets: filters.tickets,
          time_slots: filters.time_slots,
          products: filters.products,
          product_variants: filters.product_variants,
          promotions: filters.promotions,
          invitations: filters.invitations,
          discounts: filters.discounts,
          coupons: filters.coupons,
          lotteries: filters.lotteries,
          confirmed_lotteries: filters.confirmed_lotteries,
          rejected_lotteries: filters.rejected_lotteries,
          payment_statuses: filters.payment_statuses,
          assigned: filters.assigned,
        },
        participant_filters: {
          locales: filters.locales,
          mail_opt_ins: filters.mail_opt_ins,
        },
        project_id: project.id,
      },
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        onStatsChange?.(data.project.order_stats);
      },
    },
  );

  const mailPermissions = data?.project.mail_permissions || [];
  const events = data?.project.events;
  const event = events?.data.filter(({ id }) => filters.event === id)[0] || null;

  const invitations = event?.invitations || [];
  const discounts = event?.discounts || [];
  const coupons = event?.coupons || [];
  const lotteries = event?.lotteries || [];

  const stats = data?.project.order_stats;

  // Participant fields grouped by event
  const participantFields = useMemo(
    () => event?.participant_fields.filter(
      (field) => field.scope === ParticipantFieldScope.order,
    ) || [],
    [event],
  );

  const getDefaultShowingFilters = (filters: OrderFilterParams) => ({
    event: !!filters.event,
    search: !!filters.search,
    tickets: filters.ticket_categories.length > 0 || filters.tickets.length > 0 || filters.time_slots.length
      || filters.promotions.length > 0,
    time_slots: filters.time_slots.length > 0,
    products: filters.products.length > 0,
    product_variants: filters.product_variants.length > 0,
    promotions: filters.promotions.length > 0,
    invitations: filters.invitations.length > 0,
    discounts: filters.discounts.length > 0,
    coupons: filters.coupons.length > 0,
    lotteries: filters.lotteries.length > 0,
    confirmed_lotteries: filters.confirmed_lotteries.length > 0,
    rejected_lotteries: filters.rejected_lotteries.length > 0,
    payment_statuses: filters.payment_statuses.length > 0,
    participant_fields: filters.participant_fields.length > 0,
    participant_field_choices: filters.participant_field_choices.length > 0,
    assigned: filters.assigned !== null,
    mail_opt_ins: filters.mail_opt_ins?.length > 0,
    locales: filters.locales.length > 0,
  });

  // Indicates whether a filter category is collapsed or not
  const [showingFilters, setShowingFilters] = useState(() => getDefaultShowingFilters(filters));

  /**
   * Shows/hides a filter category.
   */
  const toggleShowingFilters = (key: keyof typeof showingFilters) => {
    setShowingFilters((showing) => ({
      ...showing,
      [key]: !showing[key],
    }));
  };

  const selectEvent = (event?: SelectedEvent) => {
    onChange((filters) => ({
      ...defaultFilters, // Reset all filters except search.
      search: filters.search,
      event: event?.id,
    }));
  };

  /**
   * Handles changes to the search input by storing the value in the filters.
   */
  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    onChange((filters) => ({ ...defaultFilters, ...filters, search: event.target.value || null }));
  };

  /**
   * Unsets the search term in the filters and empties the input.
   */
  const resetSearch = () => {
    onChange((filters) => ({ ...defaultFilters, ...filters, search: null }));

    searchRef.current.value = '';
  };

  // Only the keys of OrderFilterParams that have an array as value
  type OrderFilterListType = {
    [K in keyof OrderFilterParams]-?: OrderFilterParams[K] extends Array<any> ? K : never
  }[keyof OrderFilterParams];

  /**
   * Toggles a filter in a list: if it's not in the list, it's added, otherwise it's removed.
   */
  const toggleListFilter = (name: OrderFilterListType, id: string) => {
    onChange((filters) => ({
      ...defaultFilters,
      ...filters,
      [name]: toggleInList([...filters[name] || []], id),
    }));
  };

  /**
   * Toggles a ticket category filter.
   */
  const toggleTicketCategoryFilter = (ticketCategoryId: string) => {
    toggleListFilter('ticket_categories', ticketCategoryId);
  };

  /**
 * Toggles a ticket filter.
 */
  const toggleTicketFilter = (ticketId: string) => {
    if (event.ticket_categories.length === 1) {
    // If there is only one ticket category, the ticket category filter is not shown,
    // so it must be added/removed automatically.
      onChange((filters) => {
        const newTicketIds = toggleInList([...filters.tickets || []], ticketId);

        return {
          ...defaultFilters,
          ...filters,
          ticket_categories: newTicketIds.length > 0 ? [event.ticket_categories[0].id] : [],
          tickets: newTicketIds,
        };
      });
    } else {
      toggleListFilter('tickets', ticketId);
    }
  };

  /**
   * Toggles a locales filter.
   */
  const toggleLocaleFilter = (locales?: Locale) => {
    toggleListFilter('locales', locales);
  };

  /**
   * Toggles a time slot filter.
   */
  const toggleTimeSlotFilter = (timeSlotId: string) => {
    toggleListFilter('time_slots', timeSlotId);
  };

  /**
   * Toggles a product filter.
   */
  const toggleProductFilter = (productId: string) => {
    toggleListFilter('products', productId);
  };

  /**
   * Toggles a product variant filter.
   */
  const toggleProductVariantFilter = (productVariantId: string) => {
    toggleListFilter('product_variants', productVariantId);
  };

  /**
   * Toggles a promotion filter.
   */
  const togglePromotionFilter = (promotionId: string) => {
    toggleListFilter('promotions', promotionId);
  };

  /**
   * Toggles an invitation code filter.
   */
  const toggleInvitationFilter = (invitationId: string) => {
    toggleListFilter('invitations', invitationId);
  };

  /**
   * Toggles a discount filter.
   */
  const toggleDiscountFilter = (discountId: string) => {
    toggleListFilter('discounts', discountId);
  };

  /**
   * Toggles a coupon filter.
   */
  const toggleCouponFilter = (couponId: string) => {
    toggleListFilter('coupons', couponId);
  };

  /**
   * Toggles a lottery filter.
   */
  const toggleLotteryFilter = (lotteryId: string) => {
    toggleListFilter('lotteries', lotteryId);
  };

  /**
   * Toggles a confirmed lottery filter.
   */
  const toggleConfirmedLotteryFilter = (lotteryId: string) => {
    toggleListFilter('confirmed_lotteries', lotteryId);
  };

  /**
   * Toggles a rejected lottery filter.
   */
  const toggleRejectedLotteryFilter = (lotteryId: string) => {
    toggleListFilter('rejected_lotteries', lotteryId);
  };

  /**
   * Toggles a payment status filter.
   */
  const togglePaymentStatusFilter = (status: PurchasePaymentStatus) => {
    toggleListFilter('payment_statuses', status);
  };

  /**
     * Toggles a participant field filter.
     */
  const toggleParticipantFieldFilter = (participantFieldId: string) => {
    onChange((filters) => {
      const index = filters.participant_fields.findIndex(({ id }) => id === participantFieldId);

      const participantFields = index === -1
        ? [...filters.participant_fields, { id: participantFieldId }]
        : filters.participant_fields.filter(({ id }) => id !== participantFieldId);

      return {
        ...defaultFilters,
        ...filters,
        participant_fields: participantFields,
      };
    });
  };

  /**
     * Updates the filter object for a participant field.
     */
  const setParticipantFieldFilters = (
    fieldId: string,
    update: (fieldFilters: ParticipantFieldFilters) => ParticipantFieldFilters,
  ) => {
    onChange((filters) => ({
      ...defaultFilters,
      ...filters,
      participant_fields: filters.participant_fields.map((field) => (field.id === fieldId ? {
        ...field,
        filters: update(field.filters),
      } : field)),
    }));
  };

  /**
    * Toggles the 'has unassigned tickets' filter
    */
  const toggleAssignedFilter = (value: boolean | null) => {
    onChange((filters) => ({
      ...defaultFilters,
      ...filters,
      assigned: value,
    }));
  };

  /**
   * Toggles a participant field choice filter.
   */
  const toggleParticipantFieldChoiceFilter = (participantFieldChoiceId: string) => {
    toggleListFilter('participant_field_choices', participantFieldChoiceId);
  };

  /**
   * Indicates whether a user has activated any filters.
   */
  const hasFilters = Object.values(filters).some((param) => !isEmpty(param));

  /**
   * Resets all filters, UI state, and search input.
   */
  const resetFilters = (filters?: OrderFilterParams) => {
    const newFilters = { ...defaultFilters, ...filters };

    onChange(() => newFilters);
    setShowingFilters(getDefaultShowingFilters(newFilters));

    if (searchRef.current) {
      searchRef.current.value = newFilters.search || '';
    }
  };

  // Keep the filtered time slots synchronized
  useEffect(() => {
    if (event || !filters.event) {
      const possibleTickets = event?.ticket_categories.reduce(
        (tickets, ticketCategory) => [...tickets, ...ticketCategory.tickets],
        [],
      ) || [];
      const possibleTicketIds = map(possibleTickets, 'id');
      const newTicketIds = intersection(filters.tickets || [], possibleTicketIds);

      const possibleTimeSlotIds = map(flatten(map(possibleTickets.filter((ticket) => (
        filters.tickets.includes(ticket.id)
      )), 'time_slots')), 'id');

      const newTimeSlotIds = intersection(filters.time_slots, possibleTimeSlotIds);

      if (newTicketIds.join() !== filters.tickets.join()
        || newTimeSlotIds.join() !== filters.time_slots.join()) {
        onChange((filters) => ({
          ...filters,
          tickets: newTicketIds,
          time_slots: newTimeSlotIds,
        }));
      }
    }
  }, [event, filters.event, filters.tickets, filters.time_slots]);

  // Keep the filtered product variants synchronized
  useEffect(() => {
    if (event || !filters.event) {
      const possibleProducts = event?.products || [];
      const possibleProductIds = map(possibleProducts, 'id');
      const newProductIds = intersection(filters.products || [], possibleProductIds);

      const possibleProductVariantIds = map(flatten(map(possibleProducts.filter((product) => (
        filters.products.includes(product.id)
      )), 'product_variants')), 'id');

      const newProductVariantIds = intersection(filters.product_variants, possibleProductVariantIds);

      if (newProductIds.join() !== filters.products.join()
        || newProductVariantIds.join() !== filters.product_variants.join()) {
        onChange((filters) => ({
          ...filters,
          products: newProductIds,
          product_variants: newProductVariantIds,
        }));
      }
    }
  }, [event, filters.event, filters.products, filters.product_variants]);

  // Keep the filtered promotions synchronized
  useEffect(() => {
    if (event || !filters.event) {
      const possibleTicketCategories = event?.ticket_categories || [];
      const possibleTicketCategoryIds = map(possibleTicketCategories, 'id');
      const newTicketCategoryIds = intersection(filters.ticket_categories || [], possibleTicketCategoryIds);

      const possibleTickets = event?.ticket_categories.reduce(
        (tickets, ticketCategory) => (!newTicketCategoryIds.includes(ticketCategory.id)
          ? tickets
          : [...tickets, ...ticketCategory.tickets]),
        [],
      ) || [];
      const possibleTicketIds = map(possibleTickets, 'id');
      const newTicketIds = intersection(filters.tickets || [], possibleTicketIds);

      const possibleProducts = event?.products || [];
      const possibleProductIds = map(possibleProducts, 'id');
      const newProductIds = intersection(filters.products || [], possibleProductIds);

      const possiblePromotionIds = [
        ...map(flatten(map(possibleTickets.filter((ticket) => (
          filters.tickets.includes(ticket.id)
        )), 'promotions')), 'id'),
        ...map(flatten(map(possibleProducts.filter((product) => (
          filters.products.includes(product.id)
        )), 'promotions')), 'id'),
      ];

      const newPromotionIds = intersection(filters.promotions || [], possiblePromotionIds);

      if (newTicketCategoryIds.join() !== filters.ticket_categories.join()
        || newTicketIds.join() !== filters.tickets.join()
        || newProductIds.join() !== filters.products.join()
        || newPromotionIds.join() !== filters.promotions.join()) {
        onChange((filters) => ({
          ...filters,
          ticket_categories: newTicketCategoryIds,
          tickets: newTicketIds,
          products: newProductIds,
          promotions: newPromotionIds,
        }));
      }
    }
  }, [event, filters.event, filters.ticket_categories, filters.tickets, filters.products, filters.promotions]);

  // Keep the filtered participant fields and choices synchronized
  useEffect(() => {
    if (event || !filters.event) {
      const possibleFieldIds = map(participantFields, 'id');
      const selectedParticipantFieldIds = filters.participant_fields.map(({ id }) => id) || [];
      const newFieldIds = intersection(selectedParticipantFieldIds, possibleFieldIds);

      const possibleChoiceIds = map(flatten(map(participantFields.filter((field) => (
        selectedParticipantFieldIds.includes(field.id)
      )), 'choices')), 'id');

      const newChoiceIds = intersection(filters.participant_field_choices || [], possibleChoiceIds);

      if (newFieldIds.join() !== selectedParticipantFieldIds.join()
        || newChoiceIds.join() !== filters.participant_field_choices.join()) {
        onChange((filters) => ({
          ...filters,
          participant_fields: newFieldIds.map((id) => ({ id })),
          participant_field_choices: newChoiceIds,
        }));
      }
    }
  }, [event, filters.event, filters.participant_fields, filters.participant_field_choices, participantFields]);

  return (
    <UI.FormGrid sc={{ gutter: 0.75 }}>
      {enabled('event') && (
        <UI.Div>
          <EventPicker
            eventId={filters.event}
            onChange={selectEvent}
            renderActive={(event) => (
              <UI.Span sc={{ color: 'gray.800' }} style={{ fontWeight: '500' }}>
                {event?.title || t('all_events')}
              </UI.Span>
            )}
          />
        </UI.Div>
      )}

      {enabled('search') && (
        <UI.InputGroup>
          <UI.InputWrapper>
            <UI.Span>
              <UI.Icon>
                <Search />
              </UI.Icon>
            </UI.Span>
            <UI.DebouncedInput
              type="text"
              value={filters.search}
              onChange={handleSearch}
              placeholder={t('search_orders')}
              ref={searchRef}
              sc={{ px: 5 }}
              disabled={readOnly}
            />
            {filters.search && (
              <UI.Span>
                <UI.A onClick={() => resetSearch()} sc={{ color: 'gray.400' }}>
                  <UI.Icon>
                    <XCircle />
                  </UI.Icon>
                </UI.A>
              </UI.Span>
            )}
          </UI.InputWrapper>
        </UI.InputGroup>
      )}

      {event && (
        <>
          {event.ticket_categories.length > 0 && enabled('tickets') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />
              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('tickets')}
                  role="button"
                  sc={{ mb: showingFilters.tickets ? 2 : 0 }}
                >
                  {t('tickets')}
                  {' '}
                  {!showingFilters.tickets && filters.tickets.length > 0 && (
                    <UI.Badge>{filters.tickets.length}</UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.tickets && <ChevronUp />}
                    {!showingFilters.tickets && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>
                {showingFilters.tickets && event.ticket_categories.map((ticketCategory) => (
                  <UI.Div key={ticketCategory.id}>
                    {event.ticket_categories.length > 1 && (
                      <UI.Checkbox
                        checked={filters.ticket_categories.includes(ticketCategory.id)}
                        onChange={() => toggleTicketCategoryFilter(ticketCategory.id)}
                        disabled={readOnly}
                      >
                        {ticketCategory.title}
                      </UI.Checkbox>
                    )}
                    {(event.ticket_categories.length === 1
                      || filters.ticket_categories.includes(ticketCategory.id)) && (
                      <UI.Div sc={{ pl: event.ticket_categories.length > 1 ? 4 : 0 }}>
                        {ticketCategory.tickets.map((ticket) => (
                          <UI.Div key={ticket.id}>
                            <UI.Checkbox
                              checked={filters.tickets.includes(ticket.id)}
                              onChange={() => toggleTicketFilter(ticket.id)}
                              disabled={readOnly}
                            >
                              {ticket.title}
                            </UI.Checkbox>
                            {filters.tickets.includes(ticket.id)
                              && (ticket.promotions.length > 1 || ticket.time_slots.length > 0)
                              && (
                                <UI.Div sc={{ pl: 4 }}>
                                  {ticket.promotions.length > 1 && ticket.promotions.map((promotion) => (
                                    <UI.Checkbox
                                      checked={filters.promotions.includes(promotion.id)}
                                      onChange={() => togglePromotionFilter(promotion.id)}
                                      disabled={readOnly}
                                      key={promotion.id}
                                    >
                                      <UI.Icon sc={{ muted: true }}>
                                        <Tag />
                                      </UI.Icon>
                                      {' '}
                                      {promotion.title || t('standard')}
                                    </UI.Checkbox>
                                  ))}
                                  {ticket.time_slots.length > 0 && ticket.time_slots.map((timeSlot) => (
                                    <UI.Checkbox
                                      checked={filters.time_slots.includes(timeSlot.id)}
                                      onChange={() => toggleTimeSlotFilter(timeSlot.id)}
                                      disabled={readOnly}
                                      key={timeSlot.id}
                                    >
                                      <UI.Icon sc={{ muted: true }}>
                                        <Clock />
                                      </UI.Icon>
                                      {' '}
                                      <UI.Delimit>
                                        {formatTimeSlot(timeSlot)}
                                        {timeSlot.title}
                                      </UI.Delimit>
                                    </UI.Checkbox>
                                  ))}
                                </UI.Div>
                              )}
                          </UI.Div>
                        ))}
                      </UI.Div>
                    )}
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}

          {event.products.length > 0 && enabled('products') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />
              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('products')}
                  role="button"
                  sc={{ mb: showingFilters.products ? 2 : 0 }}
                >
                  {t('products')}
                  {' '}
                  {!showingFilters.products && filters.products.length > 0 && (
                  <UI.Badge>{filters.products.length}</UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.products && <ChevronUp />}
                    {!showingFilters.products && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>
                {showingFilters.products && event.products.map((product) => (
                  <UI.Div key={product.id}>
                    <UI.Checkbox
                      checked={filters.products.includes(product.id)}
                      onChange={() => toggleProductFilter(product.id)}
                      disabled={readOnly}
                    >
                      {product.title}
                    </UI.Checkbox>
                    {filters.products.includes(product.id)
                        && (product.promotions.length > 1 || product.product_variants.length > 0)
                        && (
                          <UI.Div sc={{ pl: 4 }}>
                            {product.promotions.length > 1 && product.promotions.map((promotion) => (
                              <UI.Checkbox
                                checked={filters.promotions.includes(promotion.id)}
                                onChange={() => togglePromotionFilter(promotion.id)}
                                disabled={readOnly}
                                key={promotion.id}
                              >
                                <UI.Icon sc={{ muted: true }}>
                                  <Tag />
                                </UI.Icon>
                                {' '}
                                {promotion.title || t('standard')}
                              </UI.Checkbox>
                            ))}
                            {product.product_variants.length > 0
                              && product.product_variants.map((productVariant) => (
                                <UI.Checkbox
                                  checked={filters.product_variants.includes(productVariant.id)}
                                  onChange={() => toggleProductVariantFilter(productVariant.id)}
                                  disabled={readOnly}
                                  key={productVariant.id}
                                >
                                  <SellableLabel type="product">
                                    {productVariant.title}
                                  </SellableLabel>
                                </UI.Checkbox>
                              ))}
                          </UI.Div>
                        )}
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}

          {participantFields.length > 0 && enabled('participant_fields') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />
              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('participant_fields')}
                  role="button"
                  sc={{ mb: showingFilters.participant_fields ? 2 : 0 }}
                >
                  {t('custom_fields')}
                  {' '}
                  {!showingFilters.participant_fields
                    && (filters.participant_field_choices.length > 0
                    || filters.participant_fields.length > 0) && (
                    <UI.Badge>
                      {(filters.participant_field_choices?.length || 0) + (filters.participant_fields.length || 0)}
                    </UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.participant_fields && <ChevronUp />}
                    {!showingFilters.participant_fields && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>

                {showingFilters.participant_fields && (
                  <UI.Div>
                    {participantFields.map((field) => (
                      <UI.Div key={field.id}>
                        <UI.Checkbox
                          checked={filters.participant_fields.map(({ id }) => id).includes(field.id)}
                          onChange={() => toggleParticipantFieldFilter(field.id)}
                          disabled={readOnly}
                        >
                          <UI.Span sc={{ muted: !field.enabled }}>
                            {field.title}
                          </UI.Span>
                        </UI.Checkbox>
                        {filters.participant_fields.map(({ id }) => id).includes(field.id) && (
                          <UI.Div sc={{ pl: 4 }}>
                            <ParticipantFieldFiltersForm
                              field={field}
                              filters={
                                filters.participant_fields.filter(
                                  ({ id }) => id === field.id,
                                )[0].filters
                              }
                              onChange={(update) => setParticipantFieldFilters(field.id, update)}
                            />
                            {field.choices.map((choice) => (
                              <UI.Checkbox
                                checked={filters.participant_field_choices.includes(choice.id)}
                                onChange={() => toggleParticipantFieldChoiceFilter(choice.id)}
                                disabled={readOnly}
                                key={choice.id}
                              >
                                <UI.Span sc={{ muted: !choice.enabled }}>
                                  {choice.title}
                                </UI.Span>
                              </UI.Checkbox>
                            ))}
                          </UI.Div>
                        )}
                      </UI.Div>
                    ))}
                  </UI.Div>
                )}
              </UI.InputGroup>
            </>
          )}

          {invitations.length > 0 && enabled('invitations') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />

              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('invitations')}
                  role="button"
                  sc={{ mb: showingFilters.invitations ? 2 : 0 }}
                >
                  {t('invitation_codes')}
                  {' '}
                  {!showingFilters.invitations && filters.invitations.length > 0 && (
                  <UI.Badge>
                    {filters.invitations.length}
                  </UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.invitations && <ChevronUp />}
                    {!showingFilters.invitations && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>

                {showingFilters.invitations && invitations.map((invitation) => (
                  <UI.Div key={invitation.id}>
                    <UI.Checkbox
                      checked={filters.invitations.includes(invitation.id)}
                      onChange={() => toggleInvitationFilter(invitation.id)}
                      disabled={readOnly}
                    >
                      <UI.Label>
                        <UI.Strong>{invitation.prefix}</UI.Strong>
                      </UI.Label>
                    </UI.Checkbox>
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}

          {discounts.length > 0 && enabled('discounts') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />

              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('discounts')}
                  role="button"
                  sc={{ mb: showingFilters.discounts ? 2 : 0 }}
                >
                  {t('discounts')}
                  {' '}
                  {!showingFilters.discounts && filters.discounts.length > 0 && (
                    <UI.Badge>
                      {filters.discounts.length}
                    </UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.discounts && <ChevronUp />}
                    {!showingFilters.discounts && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>

                {showingFilters.discounts && discounts.map((discount) => (
                  <UI.Div key={discount.id}>
                    <UI.Checkbox
                      checked={filters.discounts.includes(discount.id)}
                      onChange={() => toggleDiscountFilter(discount.id)}
                      disabled={readOnly}
                    >
                      {discount.title}
                    </UI.Checkbox>
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}

          {coupons.length > 0 && enabled('coupons') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />

              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('coupons')}
                  role="button"
                  sc={{ mb: showingFilters.coupons ? 2 : 0 }}
                >
                  {t('coupons')}
                  {' '}
                  {!showingFilters.coupons && filters.coupons.length > 0 && (
                    <UI.Badge>
                      {filters.coupons.length}
                    </UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.coupons && <ChevronUp />}
                    {!showingFilters.coupons && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>

                {showingFilters.coupons && coupons.map((coupon) => (
                  <UI.Div key={coupon.id}>
                    <UI.Checkbox
                      checked={filters.coupons.includes(coupon.id)}
                      onChange={() => toggleCouponFilter(coupon.id)}
                      disabled={readOnly}
                    >
                      <UI.Label>
                        <UI.Strong>{coupon.prefix}</UI.Strong>
                      </UI.Label>
                    </UI.Checkbox>
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}

          {lotteries.length > 0 && enabled('lotteries') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />

              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('lotteries')}
                  role="button"
                  sc={{ mb: showingFilters.lotteries ? 2 : 0 }}
                >
                  {t('selected_by_lottery')}
                  {' '}
                  {!showingFilters.lotteries && filters.lotteries.length > 0 && (
                    <UI.Badge>
                      {filters.lotteries.length}
                    </UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.lotteries && <ChevronUp />}
                    {!showingFilters.lotteries && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>

                {showingFilters.lotteries && lotteries.map((lottery) => (
                  <UI.Div key={lottery.id}>
                    <UI.Checkbox
                      checked={filters.lotteries.includes(lottery.id)}
                      onChange={() => toggleLotteryFilter(lottery.id)}
                      disabled={readOnly}
                    >
                      {lottery.title}
                    </UI.Checkbox>
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}

          {lotteries.length > 0 && enabled('confirmed_lotteries') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />

              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('confirmed_lotteries')}
                  role="button"
                  sc={{ mb: showingFilters.confirmed_lotteries ? 2 : 0 }}
                >
                  {t('originating_from_lottery')}
                  {' '}
                  {!showingFilters.confirmed_lotteries && filters.confirmed_lotteries.length > 0 && (
                    <UI.Badge>
                      {filters.confirmed_lotteries.length}
                    </UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.confirmed_lotteries && <ChevronUp />}
                    {!showingFilters.confirmed_lotteries && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>

                {showingFilters.confirmed_lotteries && lotteries.map((lottery) => (
                  <UI.Div key={lottery.id}>
                    <UI.Checkbox
                      checked={filters.confirmed_lotteries.includes(lottery.id)}
                      onChange={() => toggleConfirmedLotteryFilter(lottery.id)}
                      disabled={readOnly}
                    >
                      {lottery.title}
                    </UI.Checkbox>
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}

          {lotteries.length > 0 && enabled('rejected_lotteries') && (
            <>
              <UI.HR style={{ borderStyle: 'dotted' }} />

              <UI.InputGroup>
                <CategoryHeading
                  onClick={() => toggleShowingFilters('rejected_lotteries')}
                  role="button"
                  sc={{ mb: showingFilters.rejected_lotteries ? 2 : 0 }}
                >
                  {t('rejected_by_lottery')}
                  {' '}
                  {!showingFilters.rejected_lotteries && filters.rejected_lotteries.length > 0 && (
                    <UI.Badge>
                      {filters.rejected_lotteries.length}
                    </UI.Badge>
                  )}
                  {' '}
                  <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                    {showingFilters.rejected_lotteries && <ChevronUp />}
                    {!showingFilters.rejected_lotteries && <ChevronDown />}
                  </UI.Icon>
                </CategoryHeading>

                {showingFilters.rejected_lotteries && lotteries.map((lottery) => (
                  <UI.Div key={lottery.id}>
                    <UI.Checkbox
                      checked={filters.rejected_lotteries.includes(lottery.id)}
                      onChange={() => toggleRejectedLotteryFilter(lottery.id)}
                      disabled={readOnly}
                    >
                      {lottery.title}
                    </UI.Checkbox>
                  </UI.Div>
                ))}
              </UI.InputGroup>
            </>
          )}
        </>
      )}

      {enabled('assigned') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />

          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('assigned')}
              role="button"
              sc={{ mb: showingFilters.assigned ? 2 : 0 }}
            >
              {t('assignment_status')}
              {' '}
              {!showingFilters.assigned && filters.assigned !== null && (
                <UI.Badge>
                  1
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.assigned && <ChevronUp />}
                {!showingFilters.assigned && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>

            {showingFilters.assigned && (
              <UI.Div>
                <UI.Radio
                  checked={filters.assigned === null}
                  onChange={() => toggleAssignedFilter(null)}
                  disabled={readOnly}
                >
                  {t('all')}
                </UI.Radio>
                <UI.Radio
                  checked={filters.assigned === false}
                  onChange={() => toggleAssignedFilter(false)}
                  disabled={readOnly}
                >
                  {t('has_unassigned_tickets')}
                </UI.Radio>
                <UI.Radio
                  checked={filters.assigned === true}
                  onChange={() => toggleAssignedFilter(true)}
                  disabled={readOnly}
                >
                  {t('has_assigned_tickets')}
                </UI.Radio>
              </UI.Div>
            )}
          </UI.InputGroup>
        </>
      )}

      {enabled('payment_statuses') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('payment_statuses')}
              role="button"
              sc={{ mb: showingFilters.payment_statuses ? 2 : 0 }}
            >
              {t('payment_status')}
              {' '}
              {!showingFilters.payment_statuses && filters.payment_statuses.length > 0 && (
                <UI.Badge>
                  {filters.payment_statuses.length}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.payment_statuses && <ChevronUp />}
                {!showingFilters.payment_statuses && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.payment_statuses && (
              <UI.Div>
                <UI.Checkbox
                  checked={filters.payment_statuses.includes(PurchasePaymentStatus.paid)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.paid)}
                  disabled={readOnly}
                >
                  {t('paid')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={filters.payment_statuses.includes(PurchasePaymentStatus.open)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.open)}
                  disabled={readOnly}
                >
                  {t('open')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={filters.payment_statuses.includes(PurchasePaymentStatus.refunded)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.refunded)}
                  disabled={readOnly}
                >
                  {t('refunded')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={filters.payment_statuses.includes(PurchasePaymentStatus.has_balance)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.has_balance)}
                  disabled={readOnly}
                >
                  {t('credit')}
                </UI.Checkbox>
              </UI.Div>
            )}
          </UI.InputGroup>
        </>
      )}

      {enabled('locales') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('locales')}
              role="button"
              sc={{ mb: showingFilters.locales ? 2 : 0 }}
            >
              {t('preferred_language')}
              {' '}
              {!showingFilters.locales && filters.locales.length > 0 && (
              <UI.Badge>
                {filters.locales.length}
              </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.locales && <ChevronUp />}
                {!showingFilters.locales && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.locales && config.locales.map((locale) => (
              <UI.Checkbox
                key={`locales_${locale}`}
                checked={filters.locales.includes(locale)}
                onChange={() => toggleLocaleFilter(locale)}
                disabled={readOnly}
              >
                {t(`common:locales.${locale}`)}
              </UI.Checkbox>
            ))}
          </UI.InputGroup>
        </>
      )}

      {mailPermissions.length > 0 && enabled('mail_opt_ins') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('mail_opt_ins')}
              role="button"
              sc={{ mb: showingFilters.mail_opt_ins ? 2 : 0 }}
            >
              {t('mail_opt_ins')}
              {' '}
              {!showingFilters.mail_opt_ins && filters.mail_opt_ins?.length > 0 && (
                <UI.Badge>{filters.mail_opt_ins.length}</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.mail_opt_ins && <ChevronUp />}
                {!showingFilters.mail_opt_ins && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.mail_opt_ins && mailPermissions.map((mailPermission) => (
              <UI.Checkbox
                onChange={() => toggleListFilter('mail_opt_ins', mailPermission.id)}
                checked={filters.mail_opt_ins.includes(mailPermission.id)}
                disabled={readOnly}
                key={mailPermission.id}
              >
                {mailPermission.title}
              </UI.Checkbox>
            ))}
          </UI.InputGroup>
        </>
      )}

      {showTotals && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />

          <UI.Div sc={{ loading }}>
            <UI.Div sc={{ mb: 2 }}>
              <UI.Span sc={{ color: 'gray.600', inline: true }} style={{ fontWeight: 500 }}>
                <UI.Icon sc={{ mr: 1 }}>
                  <BarChart2 />
                </UI.Icon>
                {t('in_this_selection')}
              </UI.Span>
            </UI.Div>
            <UI.FlexContainer>
              <UI.Div sc={{ mr: 3 }} style={{ minWidth: 55 }}>
                <UI.H4>{formatNumber(stats?.orders || 0)}</UI.H4>
                <UI.Small>{t(stats?.orders === 1 ? 'order' : 'orders')}</UI.Small>
              </UI.Div>
            </UI.FlexContainer>
          </UI.Div>
        </>
      )}

      {showResetButton && hasFilters && (
        <UI.Div>
          <UI.Button onClick={() => resetFilters()}>
            {t('reset_filters')}
          </UI.Button>
        </UI.Div>
      )}
    </UI.FormGrid>
  );
};

const CategoryHeading = styled(UI.A)`
  ${({ theme }) => css`
    color: ${theme.colors.gray[600]};
    font-weight: 500;
    display: block;
    line-height: 21px;
  `}
`;

export default OrderFilters;
